import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import {
  createEstablishmentSite,
  createMedicalUnit,
  deleteEstablishmentSite,
  deleteMedicalUnit,
  getEstablishmentProfile,
  getHospitalRevenueSettings,
  getPharmacySettings,
  getReceptionSettings,
  listEstablishmentSites,
  listMedicalUnits,
  previewNextInvoiceNumber,
  saveEstablishmentProfile,
  saveHospitalRevenueSettings,
  savePharmacySettings,
  saveReceptionSettings,
  syncEstablishmentSettingsFromApi,
  syncEstablishmentSitesFromApi,
  syncMedicalUnitsFromApi,
  updateEstablishmentSite,
  updateMedicalUnit,
} from "@/core/admin/establishment-store";
import type { SupportedCurrency } from "@/core/admin/types";
import { downloadInvoicesCsv, listValidatedInvoices } from "@/core/pharmacy/pharmacy-store";
import { routes } from "@/router/all_routes";

const CURRENCIES: SupportedCurrency[] = ["USD", "EUR", "CDF"];

export function AdminSettingsPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([
      syncEstablishmentSettingsFromApi(),
      syncMedicalUnitsFromApi(),
      syncEstablishmentSitesFromApi(),
    ]).finally(() => setRefreshKey((k) => k + 1));
  }, []);

  const profile = useMemo(() => {
    void refreshKey;
    return getEstablishmentProfile();
  }, [refreshKey]);

  const pharmacy = useMemo(() => {
    void refreshKey;
    return getPharmacySettings();
  }, [refreshKey]);

  const reception = useMemo(() => {
    void refreshKey;
    return getReceptionSettings();
  }, [refreshKey]);

  const hospitalRevenue = useMemo(() => {
    void refreshKey;
    return getHospitalRevenueSettings();
  }, [refreshKey]);

  const units = useMemo(() => {
    void refreshKey;
    return listMedicalUnits();
  }, [refreshKey]);

  const sites = useMemo(() => {
    void refreshKey;
    return listEstablishmentSites();
  }, [refreshKey]);

  const [name, setName] = useState(profile.name);
  const [address, setAddress] = useState(profile.address);
  const [phone, setPhone] = useState(profile.phone);
  const [email, setEmail] = useState(profile.email);
  const [currency, setCurrency] = useState(pharmacy.currency);
  const [invoicePrefix, setInvoicePrefix] = useState(pharmacy.invoicePrefix);
  const [requirePatientOnInvoice, setRequirePatientOnInvoice] = useState(
    pharmacy.requirePatientOnInvoice,
  );
  const [requirePharmacistValidation, setRequirePharmacistValidation] = useState(
    pharmacy.requirePharmacistValidation,
  );
  const [syncBillingOnInvoice, setSyncBillingOnInvoice] = useState(
    pharmacy.syncBillingOnInvoice,
  );
  const [allowPharmacistDirectPrescription, setAllowPharmacistDirectPrescription] =
    useState(pharmacy.allowPharmacistDirectPrescription);
  const [allowReceptionConsultationProforma, setAllowReceptionConsultationProforma] =
    useState(reception.allowReceptionConsultationProforma);
  const [generalConsultationFee, setGeneralConsultationFee] = useState(
    String(reception.generalConsultationFee),
  );
  const [emergencyVisitFee, setEmergencyVisitFee] = useState(
    String(hospitalRevenue.emergencyVisitFee),
  );
  const [hospitalizationDailyFee, setHospitalizationDailyFee] = useState(
    String(hospitalRevenue.hospitalizationDailyFee),
  );
  const [nursingCareFee, setNursingCareFee] = useState(String(hospitalRevenue.nursingCareFee));
  const [surgeryProcedureFee, setSurgeryProcedureFee] = useState(
    String(hospitalRevenue.surgeryProcedureFee),
  );
  const [ambulanceTransportFee, setAmbulanceTransportFee] = useState(
    String(hospitalRevenue.ambulanceTransportFee),
  );
  const [unitCode, setUnitCode] = useState("");
  const [unitName, setUnitName] = useState("");
  const [unitSiteId, setUnitSiteId] = useState("");
  const [siteCode, setSiteCode] = useState("");
  const [siteName, setSiteName] = useState("");

  useEffect(() => {
    setName(profile.name);
    setAddress(profile.address);
    setPhone(profile.phone);
    setEmail(profile.email);
    setCurrency(pharmacy.currency);
    setInvoicePrefix(pharmacy.invoicePrefix);
    setRequirePatientOnInvoice(pharmacy.requirePatientOnInvoice);
    setRequirePharmacistValidation(pharmacy.requirePharmacistValidation);
    setSyncBillingOnInvoice(pharmacy.syncBillingOnInvoice);
    setAllowPharmacistDirectPrescription(pharmacy.allowPharmacistDirectPrescription);
    setAllowReceptionConsultationProforma(reception.allowReceptionConsultationProforma);
    setGeneralConsultationFee(String(reception.generalConsultationFee));
    setEmergencyVisitFee(String(hospitalRevenue.emergencyVisitFee));
    setHospitalizationDailyFee(String(hospitalRevenue.hospitalizationDailyFee));
    setNursingCareFee(String(hospitalRevenue.nursingCareFee));
    setSurgeryProcedureFee(String(hospitalRevenue.surgeryProcedureFee));
    setAmbulanceTransportFee(String(hospitalRevenue.ambulanceTransportFee));
  }, [profile, pharmacy, reception, hospitalRevenue]);

  const refresh = () => setRefreshKey((k) => k + 1);
  const flash = (msg: string) => {
    setSavedMsg(msg);
    window.setTimeout(() => setSavedMsg(null), 2500);
  };

  const handleProfileSave = (e: FormEvent) => {
    e.preventDefault();
    saveEstablishmentProfile({ name, address, phone, email });
    flash("Profil établissement enregistré.");
    refresh();
  };

  const handlePharmacySave = (e: FormEvent) => {
    e.preventDefault();
    savePharmacySettings({
      currency,
      invoicePrefix,
      requirePatientOnInvoice,
      requirePharmacistValidation,
      syncBillingOnInvoice,
      allowPharmacistDirectPrescription,
    });
    flash("Paramètres pharmacie enregistrés.");
    refresh();
  };

  const handleReceptionSave = (e: FormEvent) => {
    e.preventDefault();
    saveReceptionSettings({
      allowReceptionConsultationProforma,
      generalConsultationFee: Number(generalConsultationFee) || 0,
    });
    flash("Paramètres accueil enregistrés.");
    refresh();
  };

  const handleHospitalRevenueSave = (e: FormEvent) => {
    e.preventDefault();
    saveHospitalRevenueSettings({
      emergencyVisitFee: Number(emergencyVisitFee) || 0,
      hospitalizationDailyFee: Number(hospitalizationDailyFee) || 0,
      nursingCareFee: Number(nursingCareFee) || 0,
      surgeryProcedureFee: Number(surgeryProcedureFee) || 0,
      ambulanceTransportFee: Number(ambulanceTransportFee) || 0,
    });
    flash("Tarifs revenus hospitaliers enregistrés.");
    refresh();
  };

  const handleAddUnit = (e: FormEvent) => {
    e.preventDefault();
    if (!unitCode.trim() || !unitName.trim()) return;
    createMedicalUnit({
      code: unitCode,
      name: unitName,
      siteId: unitSiteId.trim() || null,
    });
    setUnitCode("");
    setUnitName("");
    setUnitSiteId("");
    refresh();
  };

  const handleAddSite = (e: FormEvent) => {
    e.preventDefault();
    if (!siteCode.trim() || !siteName.trim()) return;
    createEstablishmentSite({ code: siteCode, name: siteName });
    setSiteCode("");
    setSiteName("");
    refresh();
  };

  const invoiceCount = listValidatedInvoices().length;

  return (
    <div>
      <PageHeader
        title="Paramètres établissement"
        description="Services, identité de l'établissement et configuration pharmacie."
        actions={
          <Link to={routes.adminUsers} className="btn btn-outline-secondary btn-sm">
            Utilisateurs & rôles
          </Link>
        }
      />

      {savedMsg && <div className="alert alert-success py-2">{savedMsg}</div>}

      <div className="row g-3 mb-4 ph-split-workspace">
        <div className="col-lg-6">
          <form className="ph-invoice-sheet h-100" onSubmit={handleProfileSave}>
            <div className="ph-invoice-section-title">Identité</div>
            <label className="form-label small fw-semibold" htmlFor="est-name">
              Nom de l&apos;établissement
            </label>
            <input
              id="est-name"
              className="form-control form-control-sm mb-2"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <label className="form-label small fw-semibold" htmlFor="est-addr">
              Adresse
            </label>
            <input
              id="est-addr"
              className="form-control form-control-sm mb-2"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
            <label className="form-label small fw-semibold" htmlFor="est-phone">
              Téléphone
            </label>
            <input
              id="est-phone"
              className="form-control form-control-sm mb-2"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <label className="form-label small fw-semibold" htmlFor="est-email">
              E-mail
            </label>
            <input
              id="est-email"
              type="email"
              className="form-control form-control-sm mb-3"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button type="submit" className="btn btn-primary btn-sm">
              Enregistrer
            </button>
          </form>
        </div>

        <div className="col-lg-6">
          <form className="ph-invoice-sheet h-100" onSubmit={handlePharmacySave}>
            <div className="ph-invoice-section-title">Module pharmacie</div>
            <label className="form-label small fw-semibold" htmlFor="ph-currency">
              Devise
            </label>
            <select
              id="ph-currency"
              className="form-select form-select-sm mb-2"
              value={currency}
              onChange={(e) => setCurrency(e.target.value as SupportedCurrency)}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <label className="form-label small fw-semibold" htmlFor="ph-prefix">
              Préfixe numérotation factures
            </label>
            <input
              id="ph-prefix"
              className="form-control form-control-sm mb-2"
              value={invoicePrefix}
              onChange={(e) => setInvoicePrefix(e.target.value.toUpperCase())}
              maxLength={8}
            />
            <p className="small text-muted mb-3">
              Prochain numéro : <span className="ph-inv-badge">{previewNextInvoiceNumber()}</span>
            </p>
            <div className="border-top pt-3 mb-3">
              <p className="small fw-semibold mb-2">Règles de dispensation</p>
              <div className="form-check mb-2">
                <input
                  id="ph-require-patient"
                  type="checkbox"
                  className="form-check-input"
                  checked={requirePatientOnInvoice}
                  onChange={(e) => setRequirePatientOnInvoice(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="ph-require-patient">
                  Patient enregistré obligatoire (pas de vente comptoir)
                </label>
              </div>
              <div className="form-check mb-2">
                <input
                  id="ph-require-pharmacist"
                  type="checkbox"
                  className="form-check-input"
                  checked={requirePharmacistValidation}
                  onChange={(e) => setRequirePharmacistValidation(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="ph-require-pharmacist">
                  Validation pharmacien avant dispensation (ordonnances)
                </label>
              </div>
              <div className="form-check mb-2">
                <input
                  id="ph-sync-billing"
                  type="checkbox"
                  className="form-check-input"
                  checked={syncBillingOnInvoice}
                  onChange={(e) => setSyncBillingOnInvoice(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="ph-sync-billing">
                  Synchroniser automatiquement vers ITaaS Billing à la validation
                </label>
              </div>
              <div className="form-check border-top pt-2 mt-2">
                <input
                  id="ph-direct-rx"
                  type="checkbox"
                  className="form-check-input"
                  checked={allowPharmacistDirectPrescription}
                  onChange={(e) => setAllowPharmacistDirectPrescription(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="ph-direct-rx">
                  Pharmacie autonome — ordonnance directe par le pharmacien
                </label>
                <p className="small text-muted mb-0 mt-1">
                  Désactivé par défaut. Si activé, le pharmacien voit « Nouvelle ordonnance »
                  sans passage par un médecin (comptoir / officine indépendante).
                </p>
              </div>
            </div>
            <button type="submit" className="btn btn-primary btn-sm mb-3">
              Enregistrer
            </button>
            <div className="border-top pt-3">
              <p className="small fw-semibold mb-2">Export recettes</p>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={invoiceCount === 0}
                onClick={downloadInvoicesCsv}
              >
                Export CSV ({invoiceCount} facture{invoiceCount !== 1 ? "s" : ""})
              </button>
            </div>
          </form>
        </div>
      </div>

      <form className="ph-invoice-sheet mb-4" onSubmit={handleReceptionSave}>
        <div className="ph-invoice-section-title">Module accueil</div>
        <div className="form-check mb-3">
          <input
            id="rec-proforma"
            type="checkbox"
            className="form-check-input"
            checked={allowReceptionConsultationProforma}
            onChange={(e) => setAllowReceptionConsultationProforma(e.target.checked)}
          />
          <label className="form-check-label small" htmlFor="rec-proforma">
            Autoriser la proforma consultation à l&apos;accueil
          </label>
          <p className="small text-muted mb-0 mt-1">
            Désactivé par défaut. Si désactivé, les patients non assurés ne peuvent pas être
            orientés vers la clinique — le dossier part en comptabilité.
          </p>
        </div>
        <label className="form-label small fw-semibold" htmlFor="rec-fee">
          Tarif consultation généraliste
        </label>
        <input
          id="rec-fee"
          type="number"
          min={0}
          step="0.01"
          className="form-control form-control-sm mb-3"
          style={{ maxWidth: "12rem" }}
          value={generalConsultationFee}
          onChange={(e) => setGeneralConsultationFee(e.target.value)}
        />
        <button type="submit" className="btn btn-primary btn-sm">
          Enregistrer accueil
        </button>
      </form>

      <div className="ph-invoice-sheet mb-4">
        <div className="ph-invoice-section-title">Sites (multi-site soft)</div>
        <p className="small text-muted">
          Catalogue des sites de l&apos;établissement. Liaison soft via <code>siteId</code> sur
          les unités et domaines existants — pas de backfill massif.
        </p>

        <form className="row g-2 align-items-end mb-3" onSubmit={handleAddSite}>
          <div className="col-12 col-sm-6 col-md-2">
            <label className="form-label small mb-1" htmlFor="site-code">
              Code
            </label>
            <input
              id="site-code"
              className="form-control form-control-sm"
              placeholder="HQ"
              value={siteCode}
              onChange={(e) => setSiteCode(e.target.value)}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-4">
            <label className="form-label small mb-1" htmlFor="site-name">
              Nom du site
            </label>
            <input
              id="site-name"
              className="form-control form-control-sm"
              placeholder="Siège / Annexe"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-2">
            <button type="submit" className="btn btn-sm btn-outline-primary w-100">
              Ajouter
            </button>
          </div>
        </form>

        <div className="table-responsive">
          <table className="table table-sm ph-dash-table ph-diag-table mb-0">
            <thead>
              <tr>
                <th>Code</th>
                <th>Site</th>
                <th>Actif</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => (
                <tr key={site.id} className="ph-diag-row">
                  <td data-label="Code">
                    <span className="ph-inv-badge">{site.code}</span>
                  </td>
                  <td data-label="Site">{site.name}</td>
                  <td data-label="Actif">
                    <input
                      type="checkbox"
                      checked={site.active}
                      onChange={(e) => {
                        updateEstablishmentSite(site.id, { active: e.target.checked });
                        refresh();
                      }}
                    />
                  </td>
                  <td className="text-end" data-label="Actions">
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger p-0"
                      onClick={() => {
                        if (window.confirm(`Supprimer ${site.name} ?`)) {
                          deleteEstablishmentSite(site.id);
                          refresh();
                        }
                      }}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ph-invoice-sheet">
        <div className="ph-invoice-section-title">Services / unités médicales</div>
        <p className="small text-muted">
          Utilisés pour l&apos;orientation des admissions et des urgences.
        </p>

        <form className="row g-2 align-items-end mb-3" onSubmit={handleAddUnit}>
          <div className="col-12 col-sm-6 col-md-2">
            <label className="form-label small mb-1" htmlFor="unit-code">
              Code
            </label>
            <input
              id="unit-code"
              className="form-control form-control-sm"
              placeholder="URG"
              value={unitCode}
              onChange={(e) => setUnitCode(e.target.value)}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-3">
            <label className="form-label small mb-1" htmlFor="unit-name">
              Nom du service
            </label>
            <input
              id="unit-name"
              className="form-control form-control-sm"
              placeholder="Urgences"
              value={unitName}
              onChange={(e) => setUnitName(e.target.value)}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-3">
            <label className="form-label small mb-1" htmlFor="unit-site">
              Site (optionnel)
            </label>
            <select
              id="unit-site"
              className="form-select form-select-sm"
              value={unitSiteId}
              onChange={(e) => setUnitSiteId(e.target.value)}
            >
              <option value="">—</option>
              {sites
                .filter((s) => s.active)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code} — {s.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="col-12 col-sm-6 col-md-2">
            <button type="submit" className="btn btn-sm btn-outline-primary w-100">
              Ajouter
            </button>
          </div>
        </form>

        <div className="table-responsive">
          <table className="table table-sm ph-dash-table ph-diag-table mb-0">
            <thead>
              <tr>
                <th>Code</th>
                <th>Service</th>
                <th>Site</th>
                <th>Actif</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {units.map((unit) => (
                <tr key={unit.id} className="ph-diag-row">
                  <td data-label="Code">
                    <span className="ph-inv-badge">{unit.code}</span>
                  </td>
                  <td data-label="Service">{unit.name}</td>
                  <td data-label="Site">
                    {sites.find((s) => s.id === unit.siteId)?.code ?? "—"}
                  </td>
                  <td data-label="Actif">
                    <input
                      type="checkbox"
                      checked={unit.active}
                      onChange={(e) => {
                        updateMedicalUnit(unit.id, { active: e.target.checked });
                        refresh();
                      }}
                    />
                  </td>
                  <td className="text-end" data-label="Actions">
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger p-0"
                      onClick={() => {
                        if (window.confirm(`Supprimer ${unit.name} ?`)) {
                          deleteMedicalUnit(unit.id);
                          refresh();
                        }
                      }}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
